import { GoCardlessError } from "./types";

import type { BillingRequestEvents } from "./components/checkout/billing-request/types";

/**
 * Internal events are events that are emitted by the SDK itself and are not
 * intended to be consumed by the user.
 */
export interface InternalEvents {
  "api:requestStart": { path: string };
  "api:requestEnd": { path: string; status: number };
  "api:error": { path: string; error: GoCardlessError };

  // Emitted by components to report an analytics event to the SDK,
  // which will then be forwarded to the analytics subscriber
  "analytics:track": { event: string; properties: Record<string, unknown> };
}

export type NamespacedEvents<
  Namespace extends string,
  T extends Record<string, unknown>,
> = {
  [K in keyof T as `${Namespace}:${string & K}`]: T[K];
};

/**
 * Public events are events that are emitted by the SDK and are intended to be
 * consumed by the user. Each component owns and exports its own event map,
 * this type is the union consumed by the event bus and by SDK consumers.
 */
export type PublicEvents = BillingRequestEvents;

export type SdkEvents = InternalEvents & PublicEvents;

export type GoCardlessEvent = {
  [K in keyof PublicEvents]: {
    type: K;
    component: string;
    body: PublicEvents[K];
  };
}[keyof PublicEvents];

export type GoCardlessInternalEvent = {
  [K in keyof InternalEvents]: {
    type: K;
    body: InternalEvents[K];
  };
}[keyof InternalEvents];

export interface EventBus {
  // Returns an unsubscribe function.
  on<K extends keyof SdkEvents>(
    event: K,
    handler: (payload: SdkEvents[K]) => void
  ): () => void;
  emit<K extends keyof SdkEvents>(event: K, payload: SdkEvents[K]): void;
}

type Handler = (payload: unknown) => void;

export const createEventBus = (): EventBus => {
  const listeners = new Map<keyof SdkEvents, Set<Handler>>();

  const on: EventBus["on"] = (type, handler) => {
    const set = listeners.get(type) ?? new Set();

    set.add(handler as Handler);
    listeners.set(type, set);

    return () => set.delete(handler as Handler);
  };

  const emit: EventBus["emit"] = (event, payload) => {
    const handlers = listeners.get(event);

    if (!handlers) return;

    for (const handler of handlers) {
      (handler as Handler)(payload);
    }
  };

  return { on, emit };
};
